/**
 * ep-fishing-conditions-detail.js — Eprofishing "Fishing Conditions" page.
 *
 * Simplified, visual forecast dashboard (7-day overview cards + a per-day
 * detail panel: bar charts, wind-direction row, calmer-wind-windows card).
 * Shows objective wind/wave/weather data only -- no GOOD/FAIR/ROUGH rating
 * or fishing-suitability judgment; anglers judge conditions against their
 * own boat/setup/experience. The full hourly table is still available, but
 * tucked behind a collapsed "View detailed table" accordion rather than
 * shown by default.
 *
 * Data source: Open-Meteo (https://open-meteo.com) forecast + marine APIs.
 * No API key required and no key is embedded anywhere in this file -- both
 * endpoints below are Open-Meteo's public, keyless REST endpoints.
 *
 * IMPORTANT (commercial-use note): the Open-Meteo public endpoint used here
 * is suitable for testing/prototyping. Before using this on the live
 * commercial storefront, confirm Open-Meteo's API licence/commercial plan
 * requirements (https://open-meteo.com/en/pricing) or switch to an approved
 * provider/proxy.
 *
 * This file is scoped entirely to the "Fishing Conditions" page section.
 */
(function () {
  'use strict';

  var WEATHER_ENDPOINT = 'https://api.open-meteo.com/v1/forecast';
  var MARINE_ENDPOINT = 'https://marine-api.open-meteo.com/v1/marine';

  /* In-memory cache for the page session only -- keyed by "lat,lon".
     Stores the Promise (not just the resolved value) so a second click on
     a tab whose fetch is still in-flight reuses the same request instead
     of firing a duplicate one. Only the selected/default location is
     fetched on load; every other location fetches lazily, once, the first
     time its tab is clicked. */
  var cache = {};

  var COMPASS16 = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  var WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var FULL_WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var FULL_MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  var WEATHER_CODE_LABELS = {
    0: 'Clear', 1: 'Mainly Clear', 2: 'Partly Cloudy', 3: 'Overcast',
    45: 'Fog', 48: 'Fog',
    51: 'Light Drizzle', 53: 'Drizzle', 55: 'Dense Drizzle',
    56: 'Freezing Drizzle', 57: 'Freezing Drizzle',
    61: 'Light Rain', 63: 'Rain', 65: 'Heavy Rain',
    66: 'Freezing Rain', 67: 'Freezing Rain',
    71: 'Light Snow', 73: 'Snow', 75: 'Heavy Snow', 77: 'Snow Grains',
    80: 'Rain Showers', 81: 'Rain Showers', 82: 'Violent Showers',
    85: 'Snow Showers', 86: 'Snow Showers',
    95: 'Thunderstorm', 96: 'Thunderstorm', 99: 'Thunderstorm'
  };

  /* Fixed dayparts for the "Calmer Wind Windows" card -- simple named
     buckets rather than a sliding window, so the three cards always read
     as "Morning / Afternoon / Evening". Adjust the hour ranges here if
     needed. */
  var DAYPARTS = [
    { key: 'morning', label: 'Morning', startHour: 6, endHour: 10 },
    { key: 'afternoon', label: 'Afternoon', startHour: 10, endHour: 16 },
    { key: 'evening', label: 'Evening', startHour: 16, endHour: 21 }
  ];

  /* Wind speed / wave height charts show all 24 hourly points on desktop
     (there's room) but collapse to 8 points at 3-hour intervals on mobile
     (matches the approved mobile mockup exactly, avoids squeezing 24 bars
     into a narrow screen). The wind-direction row already only ever shows
     these same 8 points, at both breakpoints. */
  var ALL_HOURS = [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23];
  var THREE_HOURLY = [0, 3, 6, 9, 12, 15, 18, 21];
  var MOBILE_QUERY = '(max-width: 767px)';

  function isMobileViewport() {
    return !!(window.matchMedia && window.matchMedia(MOBILE_QUERY).matches);
  }

  function chartHourIndices() {
    return isMobileViewport() ? THREE_HOURLY : ALL_HOURS;
  }

  function weatherCodeLabel(code) {
    if (code === null || typeof code === 'undefined') return '—';
    return WEATHER_CODE_LABELS[code] || '—';
  }

  function degToCompass(deg) {
    if (deg === null || typeof deg === 'undefined' || isNaN(deg)) return '—';
    var idx = Math.round(deg / 22.5) % 16;
    if (idx < 0) idx += 16;
    return COMPASS16[idx];
  }

  function round1(n) {
    if (n === null || typeof n === 'undefined' || isNaN(n)) return null;
    return Math.round(n * 10) / 10;
  }

  function pad2(n) {
    return n < 10 ? '0' + n : '' + n;
  }

  function fmtOrDash(n) {
    return n === null || typeof n === 'undefined' ? '—' : n;
  }

  /* Minimal hand-drawn weather icons (not copied from any icon library) --
     just enough shape to read as sun / cloud / rain / storm / fog at a
     glance, in the site's cyan/navy palette. */
  function weatherIconKey(code) {
    if (code === null || typeof code === 'undefined') return 'sun';
    if (code === 0 || code === 1) return 'sun';
    if (code === 2) return 'cloud-sun';
    if (code === 3 || code === 45 || code === 48) return 'cloud';
    if (code === 95 || code === 96 || code === 99) return 'storm';
    if (code >= 51 && code <= 86) return 'rain';
    return 'sun';
  }

  function iconSvg(key, size) {
    var s = size || 28;
    var sun = '<circle cx="12" cy="12" r="4.5"></circle><path d="M12 2v2.5M12 19.5V22M4.2 4.2l1.8 1.8M18 18l1.8 1.8M2 12h2.5M19.5 12H22M4.2 19.8l1.8-1.8M18 6l1.8-1.8"></path>';
    var cloud = '<path d="M7 17.5a4 4 0 1 1 .7-7.94 5 5 0 0 1 9.6 1.44A3.5 3.5 0 0 1 17 17.5H7z"></path>';
    var cloudSun = '<circle cx="7.5" cy="7.5" r="3"></circle><path d="M7.5 2.5v1.4M7.5 11.6V13M2.5 7.5h1.4M11.6 7.5H13M3.9 3.9l1 1M10.1 10.1l1 1M11.1 3.9l-1 1M4.9 10.1l-1 1"></path><path d="M8 20.5a3.6 3.6 0 1 1 .6-7.15 4.5 4.5 0 0 1 8.65 1.3A3.15 3.15 0 0 1 17 20.5H8z"></path>';
    var rain = cloud + '<path d="M8.5 19.5l-1 2M12.5 19.5l-1 2M16.5 19.5l-1 2"></path>';
    var storm = cloud + '<path d="M13 18l-2.4 3.2h2.4L11 24"></path>';
    var paths = { sun: sun, cloud: cloud, 'cloud-sun': cloudSun, rain: rain, storm: storm };
    return '<svg viewBox="0 0 24 24" width="' + s + '" height="' + s + '" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      (paths[key] || sun) + '</svg>';
  }

  /* Simple sunrise / sun / sunset icons for the Calmer Wind Windows card. */
  function daypartIconSvg(key) {
    var horizon = '<path d="M2 18h20"></path>';
    var sunCircle = '<circle cx="12" cy="12" r="4.2"></circle><path d="M12 3v2.2M12 16.8V19M4 12h2.2M17.8 12H20M6.3 6.3l1.5 1.5M16.2 16.2l1.5 1.5M17.7 6.3l-1.5 1.5M7.8 16.2l-1.5 1.5"></path>';
    var arrowUp = '<path d="M12 15V8M9 11l3-3 3 3"></path>';
    var arrowDown = '<path d="M12 8v7M9 12l3 3 3-3"></path>';
    var map = {
      morning: '<circle cx="12" cy="15" r="3.6"></circle><path d="M12 6v3M6 15H4M20 15h-2M7.4 10.4L5.9 8.9M16.6 10.4l1.5-1.5"></path>' + horizon + arrowUp,
      afternoon: sunCircle,
      evening: '<circle cx="12" cy="15" r="3.6"></circle><path d="M6 15H4M20 15h-2M7.4 10.4L5.9 8.9M16.6 10.4l1.5-1.5"></path>' + horizon + arrowDown
    };
    return '<svg viewBox="0 0 24 24" width="24" height="24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      (map[key] || sunCircle) + '</svg>';
  }

  /* Small icons used inline next to the Average Wind / Max Gusts /
     Estimated Waves summary metrics. */
  function metricIconSvg(key) {
    var wind = '<path d="M3 8h11a3 3 0 1 0-3-3M3 16h15a3 3 0 1 1-3 3M3 12h9"></path>';
    var wave = '<path d="M2 16.5c1.5 1.3 3 1.3 4.5 0s3-1.3 4.5 0 3 1.3 4.5 0 3-1.3 4.5 0M2 12c1.5 1.3 3 1.3 4.5 0s3-1.3 4.5 0 3 1.3 4.5 0 3-1.3 4.5 0"></path>';
    return '<svg class="ep-fcd-metric-icon" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + (key === 'wave' ? wave : wind) + '</svg>';
  }

  function arrowMarkup(deg) {
    var rotation = (typeof deg === 'number' && !isNaN(deg)) ? ((deg + 180) % 360) : 0;
    return '<span class="ep-fcd-arrow-wrap">' +
      '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="transform:rotate(' + rotation + 'deg)">' +
      '<path d="M12 19V5M12 5L6 11M12 5l6 6"></path></svg>' +
      '<span class="ep-fcd-arrow-label">' + degToCompass(deg) + '</span></span>';
  }

  function buildUrl(base, params) {
    var qs = [];
    for (var key in params) {
      if (Object.prototype.hasOwnProperty.call(params, key)) {
        qs.push(encodeURIComponent(key) + '=' + encodeURIComponent(params[key]));
      }
    }
    return base + '?' + qs.join('&');
  }

  function fetchJson(url) {
    return fetch(url).then(function (res) {
      if (!res.ok) return Promise.reject(new Error('Request failed: ' + res.status));
      return res.json();
    });
  }

  function fetchDetailConditions(lat, lon) {
    var key = lat + ',' + lon;
    if (cache[key]) return cache[key];

    var weatherUrl = buildUrl(WEATHER_ENDPOINT, {
      latitude: lat,
      longitude: lon,
      hourly: 'temperature_2m,wind_speed_10m,wind_direction_10m,wind_gusts_10m,weather_code,cloud_cover,precipitation',
      forecast_days: 7,
      temperature_unit: 'celsius',
      wind_speed_unit: 'kn',
      timezone: 'auto'
    });

    var marineUrl = buildUrl(MARINE_ENDPOINT, {
      latitude: lat,
      longitude: lon,
      hourly: 'wave_height,wave_direction,wave_period,swell_wave_height',
      forecast_days: 7,
      timezone: 'auto'
    });

    var promise = Promise.all([
      fetchJson(weatherUrl),
      /* Marine data isn't essential everywhere the marine model covers --
         if it fails, still resolve so weather-only data can render rather
         than failing the whole page. */
      fetchJson(marineUrl).catch(function () { return null; })
    ]).then(function (results) {
      return { weather: results[0], marine: results[1] };
    });

    cache[key] = promise;
    promise.catch(function () { delete cache[key]; });
    return promise;
  }

  function localDateParts(utcOffsetSeconds) {
    var nowLocal = new Date(Date.now() + utcOffsetSeconds * 1000);
    return {
      dateStr: nowLocal.getUTCFullYear() + '-' + pad2(nowLocal.getUTCMonth() + 1) + '-' + pad2(nowLocal.getUTCDate()),
      hour: nowLocal.getUTCHours()
    };
  }

  /* Builds up to 7 day objects, each holding a fixed 24-slot "hours" array
     indexed by hour-of-day (0-23) -- NOT by array position in the raw API
     response -- so bar/table columns always line up with the static hour
     header regardless of any partial-day edge cases. */
  function buildDays(data) {
    var weather = data.weather;
    var marine = data.marine;
    var wh = weather.hourly;
    var offset = weather.utc_offset_seconds || 0;

    var marineByTime = {};
    if (marine && marine.hourly && marine.hourly.time) {
      var mTimes = marine.hourly.time;
      for (var i = 0; i < mTimes.length; i++) {
        marineByTime[mTimes[i]] = {
          wave: marine.hourly.wave_height ? marine.hourly.wave_height[i] : null,
          waveDir: marine.hourly.wave_direction ? marine.hourly.wave_direction[i] : null,
          wavePeriod: marine.hourly.wave_period ? marine.hourly.wave_period[i] : null,
          swell: marine.hourly.swell_wave_height ? marine.hourly.swell_wave_height[i] : null
        };
      }
    }

    var dayOrder = [];
    var dayHourSlots = {};
    for (var idx = 0; idx < wh.time.length; idx++) {
      var parts = wh.time[idx].split('T');
      var dateStr = parts[0];
      var hourNum = parseInt(parts[1].split(':')[0], 10);
      if (!dayHourSlots[dateStr]) {
        dayHourSlots[dateStr] = new Array(24);
        dayOrder.push(dateStr);
      }

      var m = marineByTime[wh.time[idx]] || {};
      var windKn = wh.wind_speed_10m ? wh.wind_speed_10m[idx] : null;
      var gustKn = wh.wind_gusts_10m ? wh.wind_gusts_10m[idx] : null;

      dayHourSlots[dateStr][hourNum] = {
        time: wh.time[idx],
        hourLabel: pad2(hourNum) + ':00',
        tempC: round1(wh.temperature_2m ? wh.temperature_2m[idx] : null),
        windKn: round1(windKn),
        windDir: wh.wind_direction_10m ? wh.wind_direction_10m[idx] : null,
        gustKn: round1(gustKn),
        code: wh.weather_code ? wh.weather_code[idx] : null,
        waveM: round1(m.wave),
        waveDir: m.waveDir,
        wavePeriod: round1(m.wavePeriod),
        swellM: round1(m.swell)
      };
    }

    var today = localDateParts(offset);
    var days = [];
    for (var d = 0; d < dayOrder.length && d < 7; d++) {
      var dateStr2 = dayOrder[d];
      var hours = dayHourSlots[dateStr2];
      var agg = aggregateDay(hours);
      days.push({
        dateStr: dateStr2,
        isToday: dateStr2 === today.dateStr,
        currentHour: dateStr2 === today.dateStr ? today.hour : null,
        hours: hours,
        avgWind: agg.avgWind,
        maxGust: agg.maxGust,
        avgWave: agg.avgWave,
        maxWave: agg.maxWave,
        dominantCode: agg.dominantCode
      });
    }
    return days;
  }

  function aggregateDay(hours) {
    var sumWind = 0, cntWind = 0, maxGust = 0, maxWave = 0, sumWave = 0, cntWave = 0;
    for (var h = 0; h < 24; h++) {
      var hour = hours[h];
      if (!hour) continue;
      if (typeof hour.windKn === 'number') { sumWind += hour.windKn; cntWind++; }
      if (typeof hour.gustKn === 'number' && hour.gustKn > maxGust) maxGust = hour.gustKn;
      if (typeof hour.waveM === 'number') {
        sumWave += hour.waveM; cntWave++;
        if (hour.waveM > maxWave) maxWave = hour.waveM;
      }
    }
    var avgWind = cntWind ? round1(sumWind / cntWind) : null;
    var avgWave = cntWave ? round1(sumWave / cntWave) : null;

    /* Representative condition for the day: local midday reads as a fair
       single snapshot without needing a full statistical mode. */
    var dominantCode = null;
    if (hours[12]) {
      dominantCode = hours[12].code;
    } else {
      for (var i = 0; i < 24; i++) {
        if (hours[i]) { dominantCode = hours[i].code; break; }
      }
    }

    return {
      avgWind: avgWind,
      maxGust: cntWind ? round1(maxGust) : null,
      avgWave: avgWave,
      maxWave: cntWave ? round1(maxWave) : null,
      dominantCode: dominantCode
    };
  }

  /* Fixed Morning/Afternoon/Evening dayparts for the "Calmer Wind
     Windows" card. Purely descriptive -- no GOOD/FAIR/ROUGH/BEST
     judgment anywhere. Whichever period has the lowest composite wind/
     gust/wave score gets a subtle neutral cyan highlight (an objective
     "this one has lower wind" signal, not a fishing-suitability
     verdict); each period's note describes how its own numbers compare
     to the day's other periods. */
  function computeDayParts(hours) {
    var periods = [];
    for (var p = 0; p < DAYPARTS.length; p++) {
      var dp = DAYPARTS[p];
      var sumWind = 0, cntWind = 0, maxGust = 0, sumWave = 0, cntWave = 0;
      var scoreSum = 0, scoreCnt = 0;
      for (var h = dp.startHour; h < dp.endHour; h++) {
        var hour = hours[h];
        var wind = hour && typeof hour.windKn === 'number' ? hour.windKn : null;
        var gust = hour && typeof hour.gustKn === 'number' ? hour.gustKn : null;
        var wave = hour && typeof hour.waveM === 'number' ? hour.waveM : null;
        if (wind !== null) { sumWind += wind; cntWind++; }
        if (gust !== null && gust > maxGust) maxGust = gust;
        if (wave !== null) { sumWave += wave; cntWave++; }
        scoreSum += (wind === null ? 999 : wind) + (gust === null ? 999 : gust) * 1.2 + (wave === null ? 99 : wave) * 10;
        scoreCnt++;
      }
      var avgWind = cntWind ? round1(sumWind / cntWind) : null;
      var avgWave = cntWave ? round1(sumWave / cntWave) : null;
      periods.push({
        key: dp.key,
        label: dp.label,
        avgWind: avgWind,
        maxGust: cntWind ? round1(maxGust) : null,
        avgWave: avgWave,
        score: scoreCnt ? scoreSum / scoreCnt : Infinity,
        timeLabel: pad2(dp.startHour) + ':00–' + pad2(dp.endHour) + ':00'
      });
    }

    var calmIdx = 0, calmScore = Infinity, strongIdx = 0, strongScore = -Infinity;
    for (var i = 0; i < periods.length; i++) {
      if (periods[i].score < calmScore) { calmScore = periods[i].score; calmIdx = i; }
      if (periods[i].score > strongScore) { strongScore = periods[i].score; strongIdx = i; }
    }

    for (var j = 0; j < periods.length; j++) {
      periods[j].isCalmer = j === calmIdx;

      var prev = j > 0 ? periods[j - 1] : null;
      var note;
      if (prev && typeof periods[j].avgWind === 'number' && typeof prev.avgWind === 'number' && (periods[j].avgWind - prev.avgWind) >= 3) {
        note = 'Wind increases during this period.';
      } else if (j === calmIdx) {
        note = 'Lighter winds during this period.';
      } else if (j === strongIdx) {
        note = 'Stronger wind expected.';
      } else {
        note = 'Moderate wind expected.';
      }

      /* Wave build-up note takes priority when this period's waves
         clearly exceed every other period's -- an objective trend, not
         a verdict. */
      if (typeof periods[j].avgWave === 'number') {
        var maxOtherWave = 0;
        for (var k = 0; k < periods.length; k++) {
          if (k !== j && typeof periods[k].avgWave === 'number' && periods[k].avgWave > maxOtherWave) {
            maxOtherWave = periods[k].avgWave;
          }
        }
        if (periods[j].avgWave > maxOtherWave + 0.2) {
          note = 'Waves may build later.';
        }
      }

      periods[j].note = note;
    }
    return periods;
  }

  /* Returns {weekday, dayMonth} rather than one combined string -- the
     daily-selector card renders these as two separate spans so CSS alone
     can lay them out side-by-side on desktop ("TUE 28 JUL") or stacked on
     two lines on mobile, without any JS/markup difference between the
     two breakpoints. */
  function formatCardDate(dateStr) {
    var d = new Date(dateStr + 'T00:00:00Z');
    return {
      weekday: WEEKDAYS[d.getUTCDay()],
      dayMonth: d.getUTCDate() + ' ' + MONTHS[d.getUTCMonth()]
    };
  }

  function formatPanelTitle(dateStr) {
    var d = new Date(dateStr + 'T00:00:00Z');
    return FULL_WEEKDAYS[d.getUTCDay()] + ', ' + d.getUTCDate() + ' ' + FULL_MONTHS[d.getUTCMonth()];
  }

  /* Lightweight vanilla SVG bar chart -- no third-party chart library.
     hourIndices picks which hours-of-day to plot (ALL_HOURS for the
     desktop 24-bar view, THREE_HOURLY for the compact 8-bar mobile
     view).

     containerWidth is measured from the real DOM element right before
     this is called (see renderDetail) and the viewBox is sized to that
     EXACT width, so the <svg> never needs to stretch non-uniformly to
     fill its container. Earlier versions built a fixed small viewBox
     (~582px for 24 bars) and stretched it to fill a much wider desktop
     panel via preserveAspectRatio="none" -- bars don't care about being
     stretched, but text glyphs do, and that mismatched X/Y scale is
     exactly what made the value labels look horizontally "stretched"/
     over-spaced. Building the chart at its real rendered width removes
     the stretch factor entirely, so labels render at their normal,
     undistorted proportions with no letter-spacing trickery needed. */
  function buildBarChart(hours, field, hourIndices, opts) {
    var n = hourIndices.length;
    var compact = n <= 10;
    var W = opts.containerWidth || (compact ? 320 : 900);
    var padL = 6, padR = 6, padT = 26, padB = 20;
    var innerW = W - padL - padR;
    var gap = compact ? 10 : 6;
    var barW = Math.max(4, (innerW - gap * (n - 1)) / n);
    var H = opts.height || 132;
    var innerH = H - padT - padB;

    var values = [];
    for (var vi = 0; vi < n; vi++) {
      var hh = hours[hourIndices[vi]];
      values.push(hh ? hh[field] : null);
    }

    var maxVal = 0;
    for (var i = 0; i < values.length; i++) {
      if (typeof values[i] === 'number' && values[i] > maxVal) maxVal = values[i];
    }
    var scaleMax = maxVal > 0 ? maxVal * 1.3 : 1;

    var peakIdx = -1, peakVal = -1;
    for (var p = 0; p < values.length; p++) {
      if (typeof values[p] === 'number' && values[p] > peakVal) { peakVal = values[p]; peakIdx = p; }
    }

    var parts = [];
    parts.push('<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" height="' + H + '" role="img" aria-hidden="true">');
    parts.push('<line x1="0" y1="' + (padT + innerH) + '" x2="' + W + '" y2="' + (padT + innerH) + '" stroke="#E2E8F0" stroke-width="1"></line>');

    for (var idx = 0; idx < n; idx++) {
      var v = values[idx];
      var hourOfDay = hourIndices[idx];
      var x = padL + idx * (barW + gap);
      var barH = typeof v === 'number' ? Math.max((v / scaleMax) * innerH, v > 0 ? 2 : 0) : 0;
      var y = padT + innerH - barH;
      var isCurrent = opts.currentIdx === hourOfDay;

      parts.push('<rect x="' + x + '" y="' + y + '" width="' + barW + '" height="' + barH + '" rx="3" fill="' + opts.color + '"' +
        (isCurrent ? ' stroke="#0F172A" stroke-width="1.5"' : '') + '></rect>');

      /* Mobile (8 points) has room to label every bar without feeling
         noisy. Desktop (24 points) only labels every 3rd hour plus the
         peak value, matching the tick marks below -- labelling all 24
         bars was the "too noisy" complaint. */
      var showLabel = typeof v === 'number' && (compact || idx % 3 === 0 || idx === peakIdx);
      if (showLabel) {
        parts.push('<text x="' + (x + barW / 2) + '" y="' + (y - 6) + '" font-size="9" font-weight="' + (idx === peakIdx ? '700' : '500') + '" fill="#0F172A" text-anchor="middle">' + v + '</text>');
      }

      var showTick = compact || idx % 3 === 0;
      if (showTick) {
        var tickLabel = pad2(hourOfDay) + ':00';
        parts.push('<text x="' + (x + barW / 2) + '" y="' + (H - 5) + '" font-size="8.5" fill="#94A3B8" text-anchor="middle">' + tickLabel + '</text>');
      }
    }

    parts.push('</svg>');
    return { markup: parts.join(''), width: W };
  }

  /* Wind-speed chart: unlike buildBarChart (still used for the wave
     chart), every displayed bar always gets both numbers -- wind speed
     above the bar, matching gust (same hour) below it -- with the time
     label under the gust value. Nothing is skipped on desktop; the
     approved mockup calls for all 24 hourly bars fully labelled, so the
     "every 3rd hour + peak" thinning buildBarChart does isn't applied
     here. Container-width measurement/viewBox sizing follows the same
     approach as buildBarChart (see its comment) to avoid SVG stretch
     distortion. */
  function buildWindChart(hours, hourIndices, opts) {
    var n = hourIndices.length;
    var compact = n <= 10;
    var W = opts.containerWidth || (compact ? 320 : 900);
    var padL = 6, padR = 6, padT = 22, padB = 34;
    var innerW = W - padL - padR;
    var gap = compact ? 10 : 6;
    var barW = Math.max(4, (innerW - gap * (n - 1)) / n);
    var H = opts.height || 140;
    var innerH = H - padT - padB;
    var axisY = padT + innerH;

    var speeds = [], gusts = [];
    for (var vi = 0; vi < n; vi++) {
      var hh = hours[hourIndices[vi]];
      speeds.push(hh ? hh.windKn : null);
      gusts.push(hh ? hh.gustKn : null);
    }

    var maxVal = 0;
    for (var i = 0; i < speeds.length; i++) {
      if (typeof speeds[i] === 'number' && speeds[i] > maxVal) maxVal = speeds[i];
    }
    var scaleMax = maxVal > 0 ? maxVal * 1.3 : 1;

    var parts = [];
    parts.push('<svg viewBox="0 0 ' + W + ' ' + H + '" width="100%" height="' + H + '" role="img" aria-hidden="true">');
    parts.push('<line x1="0" y1="' + axisY + '" x2="' + W + '" y2="' + axisY + '" stroke="#E2E8F0" stroke-width="1"></line>');

    for (var idx = 0; idx < n; idx++) {
      var v = speeds[idx];
      var g = gusts[idx];
      var hourOfDay = hourIndices[idx];
      var x = padL + idx * (barW + gap);
      var barH = typeof v === 'number' ? Math.max((v / scaleMax) * innerH, v > 0 ? 2 : 0) : 0;
      var y = axisY - barH;
      var isCurrent = opts.currentIdx === hourOfDay;
      var cx = x + barW / 2;

      parts.push('<rect x="' + x + '" y="' + y + '" width="' + barW + '" height="' + barH + '" rx="3" fill="' + opts.color + '"' +
        (isCurrent ? ' stroke="#0F172A" stroke-width="1.5"' : '') + '></rect>');

      if (typeof v === 'number') {
        parts.push('<text x="' + cx + '" y="' + (y - 6) + '" font-size="9" font-weight="600" fill="#0F172A" text-anchor="middle">' + v + '</text>');
      }

      if (typeof g === 'number') {
        parts.push('<text x="' + cx + '" y="' + (axisY + 13) + '" font-size="8" font-weight="500" fill="#334155" text-anchor="middle">' + g + '</text>');
      }

      var tickLabel = pad2(hourOfDay) + ':00';
      parts.push('<text x="' + cx + '" y="' + (axisY + 26) + '" font-size="8.5" fill="#94A3B8" text-anchor="middle">' + tickLabel + '</text>');
    }

    parts.push('</svg>');
    return { markup: parts.join(''), width: W };
  }

  function renderDailyCards(root, days, activeIndex, onSelect) {
    var container = root.querySelector('[data-fcd-daily-row]');
    if (!container) return;
    container.innerHTML = '';
    for (var i = 0; i < days.length; i++) {
      var day = days[i];
      var card = document.createElement('button');
      card.type = 'button';
      card.className = 'ep-fcd-daily-card' + (i === activeIndex ? ' is-active' : '');
      card.setAttribute('data-day-index', i);
      var cardDate = formatCardDate(day.dateStr);
      card.innerHTML =
        (day.isToday ? '<span class="ep-fcd-today-tag">TODAY</span>' : '') +
        '<span class="ep-fcd-daily-date"><span class="ep-fcd-daily-weekday">' + cardDate.weekday + '</span><span class="ep-fcd-daily-daymonth">' + cardDate.dayMonth + '</span></span>' +
        '<span class="ep-fcd-daily-icon">' + iconSvg(weatherIconKey(day.dominantCode), 26) + '</span>' +
        '<span class="ep-fcd-daily-metric"><b>' + fmtOrDash(day.avgWind) + '</b> kn avg wind</span>' +
        '<span class="ep-fcd-daily-metric"><b>' + fmtOrDash(day.maxGust) + '</b> kn gusts</span>' +
        '<span class="ep-fcd-daily-metric"><b>' + fmtOrDash(day.avgWave) + '</b> m waves</span>';
      (function (idx) {
        card.addEventListener('click', function () { onSelect(idx); });
      })(i);
      container.appendChild(card);
    }
  }

  function syncActiveState(root, activeIndex) {
    var cards = root.querySelectorAll('[data-day-index]');
    for (var i = 0; i < cards.length; i++) {
      var isActive = parseInt(cards[i].getAttribute('data-day-index'), 10) === activeIndex;
      cards[i].classList.toggle('is-active', isActive);
    }
  }

  function renderDetail(root, day, showBestWindow) {
    var hours = day.hours;

    var titleEl = root.querySelector('[data-fcd-panel-title]');
    if (titleEl) titleEl.textContent = formatPanelTitle(day.dateStr);

    var metricsEl = root.querySelector('[data-fcd-metrics]');
    if (metricsEl) {
      metricsEl.innerHTML =
        '<span class="ep-fcd-metric">' + metricIconSvg('wind') + '<span class="ep-fcd-metric-text"><span class="ep-fcd-metric-value">' + fmtOrDash(day.avgWind) + ' kn</span><span class="ep-fcd-metric-label">Average Wind</span></span></span>' +
        '<span class="ep-fcd-metric">' + metricIconSvg('wind') + '<span class="ep-fcd-metric-text"><span class="ep-fcd-metric-value">' + fmtOrDash(day.maxGust) + ' kn</span><span class="ep-fcd-metric-label">Max Gusts</span></span></span>' +
        '<span class="ep-fcd-metric">' + metricIconSvg('wave') + '<span class="ep-fcd-metric-text"><span class="ep-fcd-metric-value">' + fmtOrDash(day.avgWave) + ' m</span><span class="ep-fcd-metric-label">Waves</span></span></span>';
    }

    /* Charts are built to match their container's REAL measured width
       (see buildBarChart's comment) -- no inline width is ever hard-
       coded, so the panel's actual rendered width (full-width on
       desktop, a 1/3 grid column, a narrow mobile column) always
       determines how wide the bars spread, with no stretch distortion.
       hourIndices switches between 24 hourly points (desktop) and 8
       three-hourly points (mobile). */
    var indices = chartHourIndices();

    var windChartEl = root.querySelector('[data-fcd-chart="wind"]');
    if (windChartEl) {
      var windWidth = windChartEl.clientWidth || windChartEl.getBoundingClientRect().width;
      windChartEl.innerHTML = buildWindChart(hours, indices, { color: '#28c7ea', currentIdx: day.currentHour, containerWidth: windWidth }).markup;
    }

    var waveChartEl = root.querySelector('[data-fcd-chart="wave"]');
    if (waveChartEl) {
      var waveWidth = waveChartEl.clientWidth || waveChartEl.getBoundingClientRect().width;
      waveChartEl.innerHTML = buildBarChart(hours, 'waveM', indices, { color: '#BAE6FD', currentIdx: day.currentHour, containerWidth: waveWidth }).markup;
    }

    var dirRow = root.querySelector('[data-fcd-dir-row]');
    if (dirRow) {
      var dirHtml = '';
      for (var di = 0; di < indices.length; di++) {
        var hourOfDay = indices[di];
        var h = hours[hourOfDay];
        dirHtml += '<div class="ep-fcd-dir-item">' +
          '<span class="ep-fcd-dir-hour">' + pad2(hourOfDay) + ':00</span>' +
          arrowMarkup(h ? h.windDir : null) +
          '</div>';
      }
      dirRow.innerHTML = dirHtml;
    }

    if (showBestWindow) {
      var periodsEl = root.querySelector('[data-fcd-periods]');
      if (periodsEl) {
        var periods = computeDayParts(hours);
        var periodsHtml = '';
        for (var pI = 0; pI < periods.length; pI++) {
          var per = periods[pI];
          periodsHtml += '<div class="ep-fcd-period' + (per.isCalmer ? ' is-calmer' : '') + '">' +
            '<span class="ep-fcd-period-icon">' + daypartIconSvg(per.key) + '</span>' +
            '<span class="ep-fcd-period-label">' + per.label + '</span>' +
            '<span class="ep-fcd-period-time">' + per.timeLabel + '</span>' +
            '<span class="ep-fcd-period-explanation">' + per.note + '</span>' +
            '</div>';
        }
        periodsEl.innerHTML = periodsHtml;
      }
    }

    /* Detailed hourly table (behind the accordion) -- unchanged shape,
       just no longer visible by default. */
    var cells = {
      condition: root.querySelectorAll('[data-row="condition"] [data-col]'),
      windDir: root.querySelectorAll('[data-row="wind-dir"] [data-col]'),
      windSpeed: root.querySelectorAll('[data-row="wind-speed"] [data-col]'),
      gusts: root.querySelectorAll('[data-row="gusts"] [data-col]'),
      wave: root.querySelectorAll('[data-row="wave"] [data-col]'),
      wavePeriod: root.querySelectorAll('[data-row="wave-period"] [data-col]'),
      swell: root.querySelectorAll('[data-row="swell"] [data-col]'),
      temp: root.querySelectorAll('[data-row="temp"] [data-col]')
    };

    for (var c = 0; c < 24; c++) {
      var hc = hours[c];
      if (cells.condition[c]) cells.condition[c].textContent = hc ? weatherCodeLabel(hc.code) : '—';
      if (cells.windDir[c]) cells.windDir[c].innerHTML = arrowMarkup(hc ? hc.windDir : null);
      if (cells.windSpeed[c]) cells.windSpeed[c].textContent = hc && hc.windKn !== null ? hc.windKn + ' kn' : '—';
      if (cells.gusts[c]) cells.gusts[c].textContent = hc && hc.gustKn !== null ? hc.gustKn + ' kn' : '—';
      if (cells.wave[c]) cells.wave[c].textContent = hc && hc.waveM !== null ? hc.waveM + ' m' : '—';
      if (cells.wavePeriod[c]) cells.wavePeriod[c].textContent = hc && hc.wavePeriod !== null ? hc.wavePeriod + ' s' : '—';
      if (cells.swell[c]) cells.swell[c].textContent = hc && hc.swellM !== null ? hc.swellM + ' m' : '—';
      if (cells.temp[c]) cells.temp[c].textContent = hc && hc.tempC !== null ? hc.tempC + '°C' : '—';
    }
  }

  function setState(root, state) {
    var loading = root.querySelector('[data-fcd-loading]');
    var error = root.querySelector('[data-fcd-error]');
    var content = root.querySelector('[data-fcd-content]');
    if (loading) loading.hidden = state !== 'loading';
    if (error) error.hidden = state !== 'error';
    if (content) content.hidden = state !== 'ready';
  }

  function updateTimestamp(root) {
    var updatedEl = root.querySelector('[data-fcd-updated]');
    if (!updatedEl) return;
    var now = new Date();
    updatedEl.textContent = 'Updated ' + pad2(now.getHours()) + ':' + pad2(now.getMinutes());
  }

  function initSection(root) {
    if (root.getAttribute('data-fcd-initialized') === '1') return;
    root.setAttribute('data-fcd-initialized', '1');

    var showBestWindow = root.getAttribute('data-show-best-window') === 'true';
    var tabs = root.querySelectorAll('[data-fcd-tab]');
    if (!tabs.length) return;

    var defaultLabel = root.getAttribute('data-default-location');
    var activeTab = null;
    if (defaultLabel) {
      for (var i = 0; i < tabs.length; i++) {
        if (tabs[i].getAttribute('data-label') === defaultLabel) {
          activeTab = tabs[i];
          break;
        }
      }
    }
    if (!activeTab) activeTab = tabs[0];

    var currentDays = null;
    var currentIndex = 0;

    function onSelectDay(index) {
      currentIndex = index;
      syncActiveState(root, index);
      renderDetail(root, currentDays[index], showBestWindow);
    }

    function activateLocation(tab) {
      for (var j = 0; j < tabs.length; j++) tabs[j].classList.toggle('is-active', tabs[j] === tab);
      var lat = parseFloat(tab.getAttribute('data-lat'));
      var lon = parseFloat(tab.getAttribute('data-lon'));
      if (isNaN(lat) || isNaN(lon)) {
        setState(root, 'error');
        return;
      }

      setState(root, 'loading');
      fetchDetailConditions(lat, lon).then(function (data) {
        currentDays = buildDays(data);
        currentIndex = 0;
        /* setState('ready') un-hides [data-fcd-content] BEFORE the charts
           are built -- renderDetail measures the chart container's real
           clientWidth, which is always 0 while an ancestor is hidden, so
           building the charts first would silently fall back to a
           hardcoded nominal width instead of the panel's actual width. */
        setState(root, 'ready');
        renderDailyCards(root, currentDays, 0, onSelectDay);
        renderDetail(root, currentDays[0], showBestWindow);
        updateTimestamp(root);
      }).catch(function () {
        setState(root, 'error');
      });
    }

    for (var t = 0; t < tabs.length; t++) {
      tabs[t].addEventListener('click', function (e) {
        activateLocation(e.currentTarget);
      });
    }

    /* The wind/wave charts render at a different hourly density on
       either side of the mobile breakpoint (24 hourly bars on desktop,
       8 three-hourly bars on mobile). Re-render just the charts -- not
       a full refetch -- when the viewport actually crosses that
       breakpoint (e.g. rotating a phone, or resizing a desktop window),
       so the visible density always matches the current layout. */
    if (window.matchMedia) {
      var mql = window.matchMedia(MOBILE_QUERY);
      var onBreakpointChange = function () {
        if (currentDays && currentDays[currentIndex]) {
          renderDetail(root, currentDays[currentIndex], showBestWindow);
        }
      };
      if (mql.addEventListener) mql.addEventListener('change', onBreakpointChange);
      else if (mql.addListener) mql.addListener(onBreakpointChange);
    }

    activateLocation(activeTab);
  }

  function initAll() {
    var sections = document.querySelectorAll('[data-ep-fcd-section]');
    for (var i = 0; i < sections.length; i++) initSection(sections[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-ep-fcd-section]');
    if (root) initSection(root);
  });
})();
